import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import logo from "@/assets/logo_salone_vincente.png";
import { YouTubePlayer } from "@/components/YouTubePlayer";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { TRACKED_STEPS } from "@/config/tracking";

const CALENDLY_BOOKED_WEBHOOK =
  "https://n8n.srv1035791.hstgr.cloud/webhook/calendly-booked-matteo";
const SIGNAL_WEBHOOK =
  "https://n8n.srv1035791.hstgr.cloud/webhook/37c32c26-e8da-4146-a657-de1efd304851";

const VideoStep6 = () => {
  usePageViewTracking(TRACKED_STEPS.videoStep6);
  const [hasBooked, setHasBooked] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  console.log("[VideoStep6] URL params:", {
    groupId: searchParams.get("groupId"),
    Id: searchParams.get("Id"),
    fullQueryString: searchParams.toString(),
  });

  // Carica lo script ufficiale Calendly (necessario per ricevere postMessage)
  useEffect(() => {
    const CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";
    const JS_SRC = "https://assets.calendly.com/assets/external/widget.js";

    if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CSS_HREF;
      document.head.appendChild(link);
    }

    if (!document.querySelector(`script[src="${JS_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = JS_SRC;
      script.async = true;
      script.onload = () => console.log("[Calendly] widget script loaded");
      document.body.appendChild(script);
    } else {
      console.log("[Calendly] widget script already present");
    }
  }, []);

  // Listener per evento Calendly "event_scheduled"
  useEffect(() => {
    const isCalendlyEvent = (e: MessageEvent) =>
      typeof e.origin === "string" &&
      e.origin.includes("calendly.com") &&
      e.data &&
      typeof e.data === "object" &&
      typeof e.data.event === "string" &&
      e.data.event.indexOf("calendly.") === 0;

    const handler = (e: MessageEvent) => {
      if (!isCalendlyEvent(e)) return;

      console.log("[Calendly] event:", e.data.event, e.data);

      if (e.data.event === "calendly.event_scheduled") {
        const payload = e.data?.payload ?? {};
        const eventUri = payload?.event?.uri ?? null;
        const inviteeUri = payload?.invitee?.uri ?? null;

        setHasBooked(true);

        const groupId = searchParams.get("groupId");
        const id = searchParams.get("Id");
        const timestamp = new Date().toISOString();

        const bookedBody = {
          event: "calendly_booked",
          timestamp,
          page: "/3",
          source: "VideoStep6",
          groupId,
          Id: id,
          calendly: { eventUri, inviteeUri },
        };

        const signalBody = {
          event: "signal_sent",
          timestamp,
          page: "/3",
          source: "VideoStep6",
          groupId,
          Id: id,
          trigger: "calendly_booked",
        };

        console.log("[Calendly] booked → sending webhooks");

        // Webhook prenotazione
        fetch(CALENDLY_BOOKED_WEBHOOK, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bookedBody),
          keepalive: true,
        }).catch((err) => console.error("[Calendly] booked webhook failed:", err));

        // Webhook bonus (auto, senza click)
        fetch(SIGNAL_WEBHOOK, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signalBody),
          keepalive: true,
        }).catch((err) => console.error("[Calendly] signal webhook failed:", err));

        // Naviga automaticamente al prossimo step dopo un breve delay
        const qs = searchParams.toString();
        setTimeout(() => {
          navigate(`/community${qs ? `?${qs}` : ""}`);
        }, 2000);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [searchParams, navigate]);

  return (
    <div className="h-screen gradient-subtle flex flex-col p-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-5xl w-full flex flex-col">
          <div className="flex items-center gap-4 mb-2 md:mb-3 animate-fade-in">
            <img
              src={logo}
              alt="Salone Vincente"
              className="w-12 h-12 md:w-16 md:h-16 object-contain animate-float flex-shrink-0"
            />
            <h1 className="flex-1 text-center text-lg md:text-2xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              CONSULENZA GRATUITA PER ANALISI DEL CENTRO
            </h1>
          </div>

          <div className="relative card p-2 md:p-3 shadow-elegant animate-slide-up">
            <YouTubePlayer videoId="jdTUGBQGMxU" />
          </div>

          <div className="flex flex-col gap-3 mt-3 md:mt-4 animate-fade-in max-w-xl mx-auto w-full">
            <div
              className="calendly-inline-widget w-full rounded-xl overflow-hidden"
              data-url="https://calendly.com/matteocarassobusiness/consulenza-iniziale-matteo-carasso"
              style={{ minWidth: "320px", height: "700px" }}
            />

            {hasBooked ? (
              <div className="flex items-center gap-2 justify-center text-sm md:text-base text-green-600 font-medium">
                <CheckCircle2 className="w-5 h-5" />
                Prenotazione confermata! Ti stiamo portando al prossimo step…
              </div>
            ) : (
              <p className="text-center text-sm md:text-base text-muted-foreground italic">
                📅 Prenota un orario qui sopra: passerai automaticamente al prossimo step
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoStep6;
